import * as S from '@unionfab/ufc-shop-commons';
import { getUfcShopComposedApi, i18nFormat } from '@unionfab/ufc-shop-commons';
import { Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { Ellipsis } from '@/commons/components/Element/ellipsis';
import {
  useOrderDetailsStore,
  useOrderListStore,
  useQuoteListStore,
} from '@/stores';

import { OrderCloseButton } from '../../../../components/OrderCloseButton';
import { getNoteText } from '../../noteText';
import styles from './index.module.less';
import { OrderDetailPanel } from '../OrderDetailPanel';
import { OrderEstDeliveryAndSteps } from '../OrderEstDeliveryAndSteps';
import { ShopOrderItemReviewTable } from '../ShopOrderItemReviewTable';

export const OrderDetailMain = ({
  title,
  isQuote,
}: {
  title: string;
  isQuote: boolean;
}) => {
  const orderCode = useParams().orderCode;

  const { storeShopOrder, loadShopOrder, shopQuotation, loadingShopOrder } =
    useOrderDetailsStore(d => ({
      storeShopOrder: d.shopOrder,
      loadShopOrder: d.loadShopOrder,
      shopQuotation: d.shopQuotation,
      loadingShopOrder: d.loadingShopOrder,
    }));

  const navigate = useNavigate();

  useEffect(() => {
    if (orderCode) {
      loadShopOrder(orderCode);
    }
  }, [orderCode]);

  const { data: shopPriceInfo } = useQuery(
    ['priceInfo', storeShopOrder],
    () => {
      if (
        !storeShopOrder ||
        (storeShopOrder &&
          !['WAIT_SUBMIT', 'WAIT_REVIEW', 'WAIT_CUSTOM_CONFIRM'].includes(
            storeShopOrder.status,
          ))
      )
        return;

      const expressId = S.get(
        storeShopOrder,
        o => o.requirements.customerSelections.delivery.expressId,
      );

      return getUfcShopComposedApi().shopOrderQueryApi.calcPrice(
        storeShopOrder.code,
        {
          expressId,
        },
      );
    },
  );

  const shopOrder =
    storeShopOrder?.code === orderCode ? storeShopOrder : undefined;

  const shopOrderStatus = shopOrder?.status;

  if (!orderCode) {
    return <></>;
  }

  const quotationInsuredStr = useMemo(() => {
    if (!shopOrderStatus || !S.get(shopPriceInfo, p => p.insuredUntil))
      return <></>;

    const tz = dayjs.tz.guess();

    let formatTime = S.get(shopPriceInfo, p => p.insuredUntil);

    tz && (formatTime = dayjs(formatTime).tz(tz).format('MM/DD/YYYY HH:mm:ss'));

    const content = (
      <>
        {i18nFormat('After')}&nbsp;{formatTime}&nbsp;,
        {i18nFormat(
          'The total price maybe change, please notice the latest price',
        )}
      </>
    );

    return (
      <Typography.Paragraph
        ellipsis={{ rows: 1, tooltip: content }}
        className={styles.quotationInsuredStr}
      >
        {content}
      </Typography.Paragraph>
    );
  }, [shopOrderStatus, shopPriceInfo]);

  return (
    <div className={styles.detailMainContainer}>
      <div className={styles.detailContent}>
        <Spin spinning={loadingShopOrder}>
          {getNoteText(shopOrderStatus) !== '' && (
            <Ellipsis className={styles.noteText} maxWidth="100%">
              {getNoteText(shopOrderStatus)}
            </Ellipsis>
          )}
          <div className={styles.status}>
            <span>{i18nFormat('Status')}: </span>
            <span>
              {i18nFormat(
                S.getOrderShopStatusByInquiryOrderStatus(shopOrderStatus),
              )}
            </span>
            {quotationInsuredStr}
          </div>
          <OrderEstDeliveryAndSteps orderCode={orderCode} />
          <OrderDetailPanel
            shopPrice={shopPriceInfo}
            className={styles.detailPanel}
            orderCode={orderCode}
          />

          <ShopOrderItemReviewTable
            className={styles.reviewTable}
            order={shopOrder}
            priceInfo={shopPriceInfo}
            quotation={shopQuotation}
            isQuote={isQuote}
          />
        </Spin>
      </div>

      {shopOrder &&
        (shopOrderStatus == 'WAIT_SUBMIT' ||
          shopOrderStatus == 'WAIT_REVIEW' ||
          shopOrderStatus == 'WAIT_CUSTOM_CONFIRM') && (
          <div className={styles.closeBtn}>
            <OrderCloseButton
              buttonType="primary"
              order={shopOrder}
              onClose={() => {
                loadShopOrder(shopOrder.code);
                /** 这里清空列表页订单数据防止返回列表页时调用订单价格计算接口报错（已关闭订单无法计算）导致的 404 */
                useOrderListStore.setState({ orderList: [] });
                useQuoteListStore.setState({ orderList: [] });
                S.removeTargetUseShopQuoteStore(shopOrder.code);
                /** 关闭订单后返回上一级路由 */
                /** 判断是否为 quote 页面，如果是则返回上一级路由，否则返回订单列表页 */
                if (isQuote) {
                  navigate(-1);
                } else {
                  navigate('/orders_history/quotes');
                }
              }}
            />
          </div>
        )}
    </div>
  );
};
